import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
  Chip,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  ListAlt as ListAltIcon,
  Comment as CommentIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

const capabilityRiskLevels = [
  {
    label: "Low Dependency",
    min: 0,
    max: 6,
    color: "success",
    gradient: "linear-gradient(45deg, #e8f5e9 30%, #c8e6c9 90%)",
    description:
      "Client is largely independent and requires minimal or no assistance.",
  },
  {
    label: "Moderate Dependency",
    min: 7,
    max: 13,
    color: "warning",
    gradient: "linear-gradient(45deg, #fff3e0 30%, #ffe0b2 90%)",
    description:
      "Client requires some assistance with daily activities and may need one care worker.",
  },
  {
    label: "High Dependency",
    min: 14,
    max: 20,
    color: "error",
    gradient: "linear-gradient(45deg, #ffebee 30%, #ffcdd2 90%)",
    description:
      "Client requires significant daily assistance, often involving two care workers.",
  },
  {
    label: "Very High Dependency",
    min: 21,
    max: 28,
    color: "error",
    sx: { backgroundColor: "#b71c1c", color: "white" },
    gradient: "linear-gradient(45deg, #fbe9e7 30%, #ffab91 90%)",
    description:
      "Client is fully dependent and requires specialist equipment and monitoring.",
  },
];

const getRiskLevel = (score) => {
  if (score === null || score === undefined) return null;
  return (
    capabilityRiskLevels.find(
      (level) => score >= level.min && score <= level.max
    ) || null
  );
};

const DetailItem = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || "N/A"}
      </Typography>
    </Paper>
  </Grid>
);

const SectionCard = ({ icon, title, children }) => (
  <Card variant="outlined" sx={{ mb: 3 }}>
    <CardContent>
      <Box display="flex" alignItems="center" mb={2}>
        {icon}
        <Typography
          variant="h6"
          component="div"
          sx={{ ml: 1, fontWeight: "bold" }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </CardContent>
  </Card>
);

const CapabilityAssessmentDetailsModal = ({
  open,
  handleClose,
  assessment,
}) => {
  if (!assessment) {
    return null;
  }

  const totalScore = assessment.total_score ?? assessment.score;
  const riskLevel = getRiskLevel(totalScore);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, backgroundColor: "grey.100" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Capability Assessment
          </Typography>
          <IconButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: { xs: 1, sm: 3 }, backgroundColor: "#f7f9fc" }}
      >
        <SectionCard
          icon={<PersonIcon color="primary" />}
          title="Client Information"
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {assessment.client_name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Care Plan:</strong> {assessment.care_plan_name || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Assessed by:</strong> {assessment.created_by_name} on{" "}
                {format(new Date(assessment.created_at), "dd MMM yyyy, p")}
              </Typography>
            </Grid>
          </Grid>
        </SectionCard>

        {riskLevel && (
          <SectionCard
            icon={<AssignmentTurnedInIcon color="primary" />}
            title="Assessment Result"
          >
            <Box sx={{ p: 2, borderRadius: 2, background: riskLevel.gradient }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
              >
                <Box>
                  <Typography variant="body1">Total Score</Typography>
                  <Typography
                    variant="h3"
                    color="primary.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {totalScore}
                  </Typography>
                </Box>
                <Chip
                  label={riskLevel.label}
                  color={riskLevel.color}
                  sx={riskLevel.sx}
                  style={{ fontSize: "1rem", padding: "10px" }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {riskLevel.description}
              </Typography>
            </Box>
          </SectionCard>
        )}

        <SectionCard
          icon={<ListAltIcon color="primary" />}
          title="Detailed Breakdown"
        >
          <Grid container spacing={2}>
            <DetailItem
              label="Controlled Pain"
              value={assessment.controlled_pain_display}
            />
            <DetailItem
              label="Skin Condition"
              value={assessment.skin_condition_display}
            />
            <DetailItem
              label="Mobility / Standing"
              value={assessment.mobility_standing_display}
            />
            <DetailItem label="Walking" value={assessment.walking_display} />
            <DetailItem label="Sitting" value={assessment.sitting_display} />
            <DetailItem
              label="Movement in Bed"
              value={assessment.movement_in_bed_display}
            />
            <DetailItem
              label="Toileting / Continence"
              value={assessment.toileting_continence_display}
            />
            <DetailItem label="Bathing" value={assessment.bathing_display} />
          </Grid>
        </SectionCard>

        {assessment.comments && (
          <SectionCard icon={<CommentIcon color="primary" />} title="Comments">
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {assessment.comments}
            </Typography>
          </SectionCard>
        )}
      </DialogContent>

      <DialogActions sx={{ p: "16px 24px" }}>
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CapabilityAssessmentDetailsModal;
